import { env } from './env';

/**
 * Which surface this portal talks to.
 *
 * The server mounts five, and a token is only valid for its own - so pointing
 * this at another surface would fail with 403 rather than silently working.
 *
 * The refresh cookie is named and path-scoped per surface server-side
 * (`nudent_refresh_admin`, `/api/admin/auth`), which is what lets a staff member
 * be signed into this portal and the customer portal in the same browser without
 * one ending the other's session.
 */
export const API = {
  baseUrl: env.apiBaseUrl,
  surface: '/api/admin',
  timeoutMs: 20_000,

  endpoints: {
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
    sessions: '/auth/sessions',
    revokeOtherSessions: '/auth/sessions/revoke-others',

    // Second factor. `mfaVerify` completes a login and so takes no session;
    // the rest manage the factor for somebody already signed in.
    mfaVerify: '/auth/mfa/verify',
    mfa: '/auth/mfa',
    mfaEnroll: '/auth/mfa/enroll',
    mfaEnrollConfirm: '/auth/mfa/enroll/confirm',
    mfaDisable: '/auth/mfa/disable',

    // The estate at a glance. One call rather than four counters, so the
    // sections of the screen agree with each other.
    dashboard: '/dashboard',

    /** Every license_events row, across every organization, newest first. */
    actionLog: '/action-log',
    actionLogActors: '/action-log/actors',
    actionLogExport: '/action-log/export',

    /** Staff-wide security policy and notification toggles. */
    settings: '/settings',
    revokeAllSessions: '/settings/revoke-all-sessions',

    customers: '/customers',
    createOrganization: '/customers/organization',
    salesReps: '/customers/sales-reps',
    customer: (id: string) => `/customers/${id}`,
    issueLicense: (organizationId: string) => `/customers/${organizationId}/licenses`,
    addSeat: (organizationId: string, licenseId: string) =>
      `/customers/${organizationId}/licenses/${licenseId}/seats`,
    releaseSeat: (organizationId: string, seatId: string) =>
      `/customers/${organizationId}/seats/${seatId}/release`,
    seatHistory: (organizationId: string, seatId: string) =>
      `/customers/${organizationId}/seats/${seatId}/history`,
    revokeSeat: (organizationId: string, seatId: string) =>
      `/customers/${organizationId}/seats/${seatId}/revoke`,
    reissueSeat: (organizationId: string, seatId: string) =>
      `/customers/${organizationId}/seats/${seatId}/reissue`,
    extendLicense: (organizationId: string, licenseId: string) =>
      `/customers/${organizationId}/licenses/${licenseId}/extend`,
    emailSeatCredentials: (organizationId: string, seatId: string) =>
      `/customers/${organizationId}/seats/${seatId}/email-credentials`,
    emailPortalPassword: (id: string) => `/customers/portal-users/${id}/email-credentials`,
    portalUserStatus: (id: string) => `/customers/portal-users/${id}/status`,
    portalUserResetPassword: (id: string) => `/customers/portal-users/${id}/reset-password`,
  },
} as const;

export const apiUrl = (path: string): string => `${API.baseUrl}${API.surface}${path}`;
